import React from "react";
import { Box, Typography } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";
import { dogPossessive } from "@/helpers/dogInclusion";
import { MAX_DOGS } from "@/state/QuizContext";

/**
 * "Who's in this box" — per-dog include/exclude below the plan ladder
 * (issue #50). Non-destructive: switching a dog off keeps every answer and
 * plan value; the dog just isn't priced, charged or shipped in this box.
 */

export function InclusionSwitch({
    included,
    disabled = false,
    onDark = true,
    showLabel = true,
    label,
    onToggle,
}: {
    included: boolean;
    /** True when this is the last included dog — the switch can't turn off. */
    disabled?: boolean;
    /** Cream labels on the brown plan card; ink labels on white review cards. */
    onDark?: boolean;
    /** Hide the secondary switch label when the surrounding copy already explains the state. */
    showLabel?: boolean;
    label?: string;
    onToggle: (included: boolean) => void;
}) {
    const handle = () => {
        if (disabled && included) return;
        onToggle(!included);
    };

    const labelColor = onDark ? "rgba(250,245,235,0.85)" : QUIZ.color.statLabel;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "5px",
                flexShrink: 0,
            }}
        >
            <Box
                role="switch"
                aria-checked={included}
                aria-disabled={disabled && included}
                aria-label={label}
                tabIndex={0}
                onClick={handle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handle();
                    }
                }}
                sx={{
                    width: 51,
                    height: 31,
                    borderRadius: "999px",
                    position: "relative",
                    cursor: disabled && included ? "default" : "pointer",
                    bgcolor: included
                        ? QUIZ.color.green
                        : onDark
                            ? "rgba(250,245,235,0.3)"
                            : "rgba(51,33,21,0.18)",
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)",
                    transition: "background-color 0.2s ease",
                    opacity: disabled && included ? 0.6 : 1,
                    "&:focus-visible": {
                        outline: `2px solid ${onDark ? QUIZ.color.cream : QUIZ.color.brown}`,
                        outlineOffset: "2px",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "2px",
                        left: included ? "22px" : "2px",
                        width: 27,
                        height: 27,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        boxShadow: "0 3px 8px rgba(0,0,0,0.15), 0 1px 1px rgba(0,0,0,0.16)",
                        transition: "left 0.2s ease",
                    },
                }}
            />
            {showLabel && (
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: "10.5px",
                        fontWeight: 600,
                        color: labelColor,
                        whiteSpace: "nowrap",
                        userSelect: "none",
                    }}
                >
                    {included ? "Included" : "Not included"}
                </Typography>
            )}
        </Box>
    );
}

export type WhoRow = {
    dogId: string;
    name: string;
    sex?: unknown;
    included: boolean;
    /** Effective daily grams for the selected plan (dailyKey). */
    dailyGrams: number;
    /** Discounted meals subtotal for this dog on the selected plan. */
    discount: number;
    /** Full-price meals subtotal for this dog on the selected plan. */
    full: number;
};

const COUNT_WORDS = ["", "one", "two", "three", "four", "five"];

function perDayForCopy(includedCount: number, totalCount: number): string {
    const word = COUNT_WORDS[includedCount] ?? String(includedCount);
    if (includedCount === 1) return "for one dog";
    if (includedCount === totalCount && includedCount >= 3) return `for all ${word}`;
    return `for ${word} dogs`;
}

/**
 * Object-form pronoun ("her meals", "his meals", "their meals") derived from
 * the same sex value dogPossessive() uses, so the two never disagree.
 */
function dogObjectPronoun(sex: unknown): "him" | "her" | "them" {
    const possessive = dogPossessive(sex);
    if (possessive === "his") return "him";
    if (possessive === "her") return "her";
    return "them";
}

export default function WhoIsInBoxBlock({
    rows,
    days,
    totalDiscount,
    onToggle,
    onAddDog,
}: {
    rows: WhoRow[];
    days: number;
    /** Household total (discounted) across included dogs for the selected plan. */
    totalDiscount: number;
    onToggle: (dogId: string, included: boolean) => void;
    onAddDog: () => void;
}) {
    const fmt = (n: number) => `£${(n || 0).toFixed(2)}`;

    const includedRows = rows.filter((r) => r.included);
    const includedCount = includedRows.length;
    const totalFull = includedRows.reduce((sum, r) => sum + r.full, 0);
    const perDayAll = days > 0 ? totalDiscount / days : 0;

    const excludedRows = rows.filter((r) => !r.included);

    return (
        <Box sx={{ mt: { xs: "16px", md: "24px" } }}>
        <Box
            sx={{
                background: "rgba(250, 245, 235, 0.08)",
                border: "1px solid rgba(250, 245, 235, 0.24)",
                borderRadius: "14px",
                p: "14px",
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: "4px" }}>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.label,
                        fontSize: { xs: 17, md: 19 },
                        color: QUIZ.color.cream,
                    }}
                >
                    Who's in this box
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "rgba(250,245,235,0.7)",
                        whiteSpace: "nowrap",
                    }}
                >
                    {includedCount === rows.length
                        ? `${rows.length} dog${rows.length === 1 ? "" : "s"}`
                        : `${includedCount} of ${rows.length} dogs`}
                </Typography>
            </Box>

            {/* Per-dog rows */}
            {rows.map((row, index) => {
                const perDay = days > 0 ? row.discount / days : 0;
                const lastIncluded = row.included && includedCount === 1;
                const isLastRow = index === rows.length - 1;

                return (
                    <Box
                        key={row.dogId}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "11px",
                            py: "11px",
                            borderBottom: isLastRow ? "none" : "1px solid rgba(250,245,235,0.14)",
                            opacity: row.included ? 1 : 0.55,
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: QUIZ.font.body,
                                    fontWeight: 700,
                                    fontSize: "15.5px",
                                    color: QUIZ.color.cream,
                                    lineHeight: 1.25,
                                }}
                            >
                                {row.name}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: QUIZ.font.body,
                                    fontSize: "12.5px",
                                    color: "rgba(250,245,235,0.7)",
                                    mt: "2px",
                                }}
                            >
                                {row.included
                                    ? `${row.dailyGrams}g a day · ${fmt(perDay)} per day`
                                    : "Not in this box"}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: QUIZ.font.body,
                                    fontWeight: 800,
                                    fontSize: "15.5px",
                                    color: QUIZ.color.cream,
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    textDecoration: row.included ? "none" : "line-through",
                                }}
                            >
                                {fmt(row.discount)}
                            </Typography>
                            {row.included && row.full > row.discount && (
                                <Typography
                                    sx={{
                                        fontFamily: QUIZ.font.body,
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        color: "#f2695c",
                                        textDecoration: "line-through",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {fmt(row.full)}
                                </Typography>
                            )}
                        </Box>

                        <InclusionSwitch
                            included={row.included}
                            disabled={lastIncluded}
                            label={`${row.name} included in this box`}
                            onToggle={(next) => onToggle(row.dogId, next)}
                        />
                    </Box>
                );
            })}

            {/* Reassurance line for excluded dogs — copy matches design:
                "{Name} isn't in this box. Click the toggle to add {pronoun}
                meals back in. We've kept {pronoun} answers and plan." */}
            {excludedRows.map((row) => {
                const pronoun = dogObjectPronoun(row.sex);
                const possessive = dogPossessive(row.sex);
                return (
                    <Box
                        key={`excl-${row.dogId}`}
                        sx={{
                            bgcolor: "rgba(250,245,235,0.12)",
                            borderRadius: "10px",
                            p: "10px 12px",
                            mt: "12px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                fontSize: "12px",
                                color: QUIZ.color.cream,
                                lineHeight: 1.5,
                            }}
                        >
                            <strong>{row.name} isn't in this box.</strong> Click the toggle to
                            add {pronoun} meals back in. We've kept {possessive} answers and
                            plan.
                        </Typography>
                    </Box>
                );
            })}

            {/* Quiet add link — hidden at the household cap (ADD_DOG refuses there too) */}
            {rows.length < MAX_DOGS && (
            <Box
                role="button"
                tabIndex={0}
                onClick={onAddDog}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onAddDog();
                    }
                }}
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: "12.5px",
                    color: QUIZ.color.cream,
                    opacity: 0.8,
                    textAlign: "center",
                    mt: "12px",
                    pt: "12px",
                    borderTop: "1px solid rgba(250,245,235,0.16)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { opacity: 1 },
                }}
            >
                + Add another dog
            </Box>
            )}
        </Box>

        {/* Total today — sits outside the bordered "Who's in this box" panel */}
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: "10px",
                mt: "16px",
                bgcolor: QUIZ.color.cream2,
                borderRadius: "12px",
                p: "13px 14px",
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontWeight: 800,
                        fontSize: "15px",
                        color: QUIZ.color.ink,
                    }}
                >
                    Total today
                </Typography>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: "12px",
                        color: QUIZ.color.grey,
                        mt: "2px",
                    }}
                >
                    {fmt(perDayAll)} a day {perDayForCopy(includedCount, rows.length)}
                </Typography>
            </Box>
            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.display,
                        fontWeight: 900,
                        fontSize: "20px",
                        color: QUIZ.color.ink,
                        whiteSpace: "nowrap",
                        lineHeight: 1.2,
                    }}
                >
                    {fmt(totalDiscount)}
                </Typography>
                {totalFull > totalDiscount && (
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#f2695c",
                            textDecoration: "line-through",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fmt(totalFull)}
                    </Typography>
                )}
            </Box>
        </Box>
        </Box>
    );
}